/**
 * Em que estado um bloco da home está, do ponto de vista de orientar o
 * Participante.
 *
 * Antes desta change havia um predicado só por bloco — o `isEmpty` de cada
 * hook — e ele misturava estados que pedem respostas opostas. `unconfigured`
 * não tem o que explicar; `no-progress` tem; `completed` não precisa.
 * `in-progress` existe para que nenhum caso caia por omissão nos outros três.
 *
 * Todas as funções aqui são puras: recebem números e devolvem o estado. Quem
 * lê os hooks é `useBlockGuidance`.
 */
export type BlockEmptiness =
  | { kind: 'unconfigured' }
  | { kind: 'no-progress' }
  | { kind: 'completed' }
  | { kind: 'in-progress' }

const UNCONFIGURED: BlockEmptiness = { kind: 'unconfigured' }
const NO_PROGRESS: BlockEmptiness = { kind: 'no-progress' }
const COMPLETED: BlockEmptiness = { kind: 'completed' }
const IN_PROGRESS: BlockEmptiness = { kind: 'in-progress' }

export const medalsEmptiness = ({
  total,
  unlockedCount
}: {
  total: number
  unlockedCount: number
}): BlockEmptiness => {
  if (total === 0) return UNCONFIGURED
  if (unlockedCount === 0) return NO_PROGRESS
  if (unlockedCount >= total) return COMPLETED
  return IN_PROGRESS
}

/**
 * Ranking não tem `unconfigured` próprio.
 *
 * O estado existe — evento sem nenhum recurso pontuável —, mas é suprimido a
 * jusante: `buildRankingGuidance` devolve lista vazia e a porta não aparece.
 * Um ramo aqui seria um segundo caminho para o mesmo desfecho.
 */
export const rankingEmptiness = ({
  isEmpty
}: {
  isEmpty: boolean
}): BlockEmptiness => (isEmpty ? NO_PROGRESS : IN_PROGRESS)

/**
 * Usa `pendingMissionsCount`, nunca `allNextMissions.length`.
 *
 * `useHomeView` promove a primeira missão pendente a destaque quando não há
 * jornada pendente, e então a remove de `allNextMissions`. Num evento com uma
 * única missão acionável aquela lista fica vazia — e o bloco anunciaria
 * ausência de missão com a missão em destaque logo acima.
 */
export const missionsEmptiness = ({
  missionsTotal,
  pendingMissionsCount
}: {
  missionsTotal: number
  pendingMissionsCount: number
}): BlockEmptiness => {
  if (missionsTotal === 0) return UNCONFIGURED
  if (pendingMissionsCount === 0) return NO_PROGRESS
  return IN_PROGRESS
}
